import type { FC } from 'react';
import { memo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import type { ITheme } from '../../../utils/theme';
import { TypographyVariant } from '../../../utils/theme';
import Typography from '../../typography/typography';
import {
  getCurrencyBorder,
  getCurrencyColor,
} from '../helper-functions/currency';

interface CurrencyPrefixProps {
  currency: string;
  symbol: string;
  hasError: boolean;
  isFocused: boolean;
  disabled: boolean;
  height?: string;
}

const CurrencyPrefix: FC<CurrencyPrefixProps> = ({
  currency,
  symbol,
  hasError,
  isFocused,
  disabled,
  ...rest
}) => {
  const theme = useTheme();

  return (
    <CurrencyWrapper
      $hasError={hasError}
      $isFocused={isFocused}
      $symbol={symbol}
      $isDisabled={disabled}
      {...rest}
    >
      <Typography
        variant={TypographyVariant.bodyMd2}
        color={getCurrencyColor(hasError, disabled, theme)}
      >
        {currency}
      </Typography>
    </CurrencyWrapper>
  );
};

export default memo(CurrencyPrefix);

const CurrencyWrapper = styled.div`
  ${({
    theme,
    $hasError,
    height,
    $isFocused,
    $isDisabled,
    $symbol,
  }: {
    theme: ITheme;
    $hasError: boolean;
    height?: string;
    $isFocused: boolean;
    $symbol: string;
    $isDisabled: boolean;
  }) => css`
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${theme.spacing(6.5)};
    min-height: ${height ?? theme.input.text.height};
    position: relative;
    height: ${height ?? theme.input.text.height};
    border-radius: ${theme.borderRadius.sm} 0 0 ${theme.borderRadius.sm};
    margin-top: ${height
      ? `calc(${height} - 2 * ${height})`
      : `-${theme.input.text.height}`};
    z-index: 1;
    border: ${getCurrencyBorder($hasError, $isFocused, $isDisabled, theme)};
    border-right: none;
    font-size: 14px;

    :before {
      content: '';
      position: absolute;
      right: 0;
      bottom: ${theme.spacing(1)};
      top: ${theme.spacing(1)};
      width: 1px;
      background-color: ${theme.palette.neutral['60']};
    }

    :after {
      content: '${$symbol}';
      position: absolute;
      left: calc(100% + 10px);
      font-weight: 400;
      color: ${$hasError
        ? theme.palette.error.main
        : theme.palette.neutral[100]};
    }
  `}
`;
